import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import Sidebar from "@/components/Sidebar";
import MT5Status from "@/components/MT5Status";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sync, setSync] = useState(null);
  const { user, logout } = useAuth();
  const location = useLocation();

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const load = () => api.get("/sync/status").then((r) => setSync(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Desktop sidebar */}
      <aside className="fixed left-0 top-0 z-30 hidden h-full w-64 border-r border-gold/10 bg-[#09090A] lg:block">
        <Sidebar />
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden" />
            <motion.aside
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="fixed left-0 top-0 z-50 h-full w-72 border-r border-gold/15 bg-[#09090A] lg:hidden">
              <button onClick={() => setMobileOpen(false)}
                className="absolute right-4 top-5 text-zinc-500" data-testid="close-mobile-nav">
                <X size={20} />
              </button>
              <Sidebar onNavigate={() => setMobileOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-20 border-b border-gold/10 bg-[#050505]/85 backdrop-blur-xl">
          <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-8">
            <div className="flex items-center gap-3">
              <button onClick={() => setMobileOpen(true)}
                className="text-zinc-300 lg:hidden" data-testid="open-mobile-nav">
                <Menu size={22} />
              </button>
              <img src="/deluxe-logo-main.jpeg" alt="DELUXE" className="h-7 lg:hidden" />
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <MT5Status sync={sync} />
              <div className="hidden items-center gap-2 border-l border-white/10 pl-4 sm:flex">
                <div className="text-right">
                  <div className="font-sans text-xs text-white">{user?.name}</div>
                  <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">{user?.role}</div>
                </div>
                <button onClick={logout} data-testid="logout-btn"
                  className="rounded-md border border-white/10 p-2 text-zinc-400 transition-colors hover:border-loss/40 hover:text-loss">
                  <LogOut size={15} />
                </button>
              </div>
              <button onClick={logout}
                className="rounded-md border border-white/10 p-2 text-zinc-400 sm:hidden" data-testid="logout-btn-mobile">
                <LogOut size={15} />
              </button>
            </div>
          </div>
        </header>

        <main className="px-4 py-6 sm:px-8 sm:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
